import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DOMPurify from 'dompurify';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Table, TableBody, TableCell, TableHead,
    TableHeader, TableRow
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogFooter, DialogTrigger
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem,
    SelectTrigger, SelectValue
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
    Users, Crown, MessageSquare, Tag, TrendingUp,
    Search, Trash2, Edit, Plus, Power, ChevronLeft,
    ChevronRight, Shield, BarChart3, Database, Play,
    RefreshCw, CheckCircle, XCircle, Clock, AlertCircle,
    BookOpen, Megaphone, Eye, MousePointer, DollarSign,
    ExternalLink, Calendar, FileText, Save, Globe, EyeOff
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('overview');
    const headers = { Authorization: `Bearer ${token}` };

    return (
        <div className="min-h-screen bg-background" data-testid="admin-dashboard">
            <Navbar />
            <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-12">
                <div className="flex items-center gap-3 mb-8">
                    <Shield className="w-7 h-7 text-secondary" />
                    <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
                        Admin Dashboard
                    </h1>
                </div>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6 bg-muted/50" data-testid="admin-tabs">
                        <TabsTrigger value="overview" data-testid="tab-overview">
                            <BarChart3 className="w-4 h-4 mr-2" />Overview
                        </TabsTrigger>
                        <TabsTrigger value="users" data-testid="tab-users">
                            <Users className="w-4 h-4 mr-2" />Users
                        </TabsTrigger>
                        <TabsTrigger value="codes" data-testid="tab-codes">
                            <Tag className="w-4 h-4 mr-2" />Discount Codes
                        </TabsTrigger>
                        <TabsTrigger value="knowledge" data-testid="tab-knowledge">
                            <Database className="w-4 h-4 mr-2" />Knowledge Base
                        </TabsTrigger>
                        <TabsTrigger value="ads" data-testid="tab-ads">
                            <Megaphone className="w-4 h-4 mr-2" />Ad Manager
                        </TabsTrigger>
                        <TabsTrigger value="blog" data-testid="tab-blog">
                            <FileText className="w-4 h-4 mr-2" />Blog
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="overview"><OverviewTab headers={headers} /></TabsContent>
                    <TabsContent value="users"><UsersTab headers={headers} /></TabsContent>
                    <TabsContent value="codes"><CodesTab headers={headers} /></TabsContent>
                    <TabsContent value="knowledge"><KnowledgeBaseTab headers={headers} /></TabsContent>
                    <TabsContent value="ads"><AdsManagerTab headers={headers} /></TabsContent>
                    <TabsContent value="blog"><BlogTab headers={headers} /></TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

function OverviewTab({ headers }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}/admin/stats`, { headers })
            .then(r => r.json()).then(setStats)
            .catch(() => toast.error('Failed to load stats'))
            .finally(() => setLoading(false));
    }, [headers]);

    if (loading) return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
                <Card key={i} className="border-border/50"><CardContent className="pt-5 pb-4 px-5">
                    <div className="h-4 w-20 bg-muted rounded mb-3 animate-pulse" />
                    <div className="h-8 w-12 bg-muted rounded animate-pulse" />
                </CardContent></Card>
            ))}
        </div>
    );
    if (!stats) return null;

    const cards = [
        { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-primary' },
        { label: 'Premium Users', value: stats.premium_users, icon: Crown, color: 'text-secondary' },
        { label: 'Free Users', value: stats.free_users, icon: Users, color: 'text-muted-foreground' },
        { label: 'New (7d)', value: stats.new_users_7d, icon: TrendingUp, color: 'text-primary' },
        { label: 'New (30d)', value: stats.new_users_30d, icon: TrendingUp, color: 'text-secondary' },
        { label: 'Chat Sessions', value: stats.total_chats, icon: MessageSquare, color: 'text-primary' },
        { label: 'Ustadh Sessions', value: stats.total_sessions, icon: Crown, color: 'text-secondary' },
        { label: 'Active Codes', value: stats.active_discount_codes, icon: Tag, color: 'text-primary' },
    ];

    return (
        <div data-testid="overview-content">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {cards.map((c, i) => (
                    <Card key={i} className="border-border/50" data-testid={`stat-card-${i}`}>
                        <CardContent className="pt-5 pb-4 px-5">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-muted-foreground uppercase tracking-wider">{c.label}</span>
                                <c.icon className={`w-4 h-4 ${c.color}`} />
                            </div>
                            <p className="text-2xl font-bold font-heading">{c.value}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>
            {stats.subscription_breakdown && Object.keys(stats.subscription_breakdown).length > 0 && (
                <Card className="mt-6 border-border/50">
                    <CardHeader><CardTitle className="text-lg">Subscription Breakdown</CardTitle></CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-4">
                            {Object.entries(stats.subscription_breakdown).map(([plan, count]) => (
                                <div key={plan} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted/50">
                                    <Badge variant={plan === 'free' ? 'secondary' : 'default'}>{plan}</Badge>
                                    <span className="font-bold">{count}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

function UsersTab({ headers }) {
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [editUser, setEditUser] = useState(null);
    const [editPlan, setEditPlan] = useState('');

    const fetchUsers = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams({ page, limit: 20 });
        if (search) params.set('search', search);
        if (statusFilter) params.set('status', statusFilter);
        fetch(`${API}/admin/users?${params}`, { headers })
            .then(r => r.json())
            .then(data => { setUsers(data.users || []); setTotal(data.total || 0); setPages(data.pages || 1); })
            .catch(() => toast.error('Failed to load users'))
            .finally(() => setLoading(false));
    }, [page, search, statusFilter, headers]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const handleDelete = async (userId, name) => {
        if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
        try {
            const res = await fetch(`${API}/admin/users/${userId}`, { method: 'DELETE', headers });
            if (!res.ok) throw new Error();
            toast.success('User deleted'); fetchUsers();
        } catch { toast.error('Failed to delete user'); }
    };

    const handleUpdate = async () => {
        if (!editUser) return;
        try {
            const res = await fetch(`${API}/admin/users/${editUser.id}`, {
                method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ subscription_status: editPlan })
            });
            if (!res.ok) throw new Error();
            toast.success('User updated'); setEditUser(null); fetchUsers();
        } catch { toast.error('Failed to update user'); }
    };

    return (
        <div data-testid="users-content">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search by name, email, or phone..." className="pl-9" value={search}
                        onChange={e => { setSearch(e.target.value); setPage(1); }} data-testid="user-search-input" />
                </div>
                <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="w-[180px]" data-testid="user-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="ustadh_monthly">Monthly</SelectItem>
                        <SelectItem value="ustadh_yearly">Yearly</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <Card className="border-border/50 overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead className="hidden sm:table-cell">Email</TableHead>
                            <TableHead className="hidden lg:table-cell">Phone</TableHead>
                            <TableHead>Plan</TableHead>
                            <TableHead className="hidden md:table-cell">Joined</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? Array.from({ length: 5 }).map((_, i) => (
                            <TableRow key={i}>
                                <TableCell><div className="h-4 w-24 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell className="hidden sm:table-cell"><div className="h-4 w-32 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell className="hidden lg:table-cell"><div className="h-4 w-24 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell><div className="h-5 w-16 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell className="hidden md:table-cell"><div className="h-4 w-20 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell />
                            </TableRow>
                        )) : users.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No users found</TableCell></TableRow>
                        ) : users.map(u => (
                            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">{u.name}</span>
                                        {u.is_admin && <Shield className="w-3.5 h-3.5 text-secondary" />}
                                    </div>
                                </TableCell>
                                <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">{u.email}</TableCell>
                                <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">{u.phone || '-'}</TableCell>
                                <TableCell>
                                    <Badge variant={u.subscription_status === 'free' ? 'outline' : 'default'}>{u.subscription_status}</Badge>
                                </TableCell>
                                <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                                    {new Date(u.created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => { setEditUser(u); setEditPlan(u.subscription_status); }}
                                            data-testid={`edit-user-${u.id}`}><Edit className="w-4 h-4" /></Button>
                                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"
                                            onClick={() => handleDelete(u.id, u.name)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>
            <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
                <span>{total} user{total !== 1 ? 's' : ''}</span>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page"><ChevronLeft className="w-4 h-4" /></Button>
                    <span>Page {page} of {pages}</span>
                    <Button variant="outline" size="icon" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="next-page"><ChevronRight className="w-4 h-4" /></Button>
                </div>
            </div>
            <Dialog open={!!editUser} onOpenChange={open => { if (!open) setEditUser(null); }}>
                <DialogContent data-testid="edit-user-dialog">
                    <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
                    {editUser && (
                        <div className="space-y-4 py-2">
                            <div><Label className="text-muted-foreground text-xs">Name</Label><p className="font-medium">{editUser.name}</p></div>
                            <div><Label className="text-muted-foreground text-xs">Email</Label><p className="text-sm">{editUser.email}</p></div>
                            <div><Label className="text-muted-foreground text-xs">Phone</Label><p className="text-sm">{editUser.phone || 'Not provided'}</p></div>
                            <div>
                                <Label>Subscription Plan</Label>
                                <Select value={editPlan} onValueChange={setEditPlan}>
                                    <SelectTrigger className="mt-1" data-testid="edit-plan-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="free">Free</SelectItem>
                                        <SelectItem value="ustadh_monthly">Ustadh Monthly</SelectItem>
                                        <SelectItem value="ustadh_yearly">Ustadh Yearly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
                        <Button onClick={handleUpdate} data-testid="save-user-btn">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function CodesTab({ headers }) {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // {id, code} for delete confirmation
    const [deleting, setDeleting] = useState(false);
    const [form, setForm] = useState({ code: '', discount_percent: 10, max_uses: '', description: '', expires_at: '' });

    const fetchCodes = useCallback(() => {
        setLoading(true);
        fetch(`${API}/admin/discount-codes`, { headers })
            .then(r => r.json()).then(data => setCodes(data.codes || []))
            .catch(() => toast.error('Failed to load codes'))
            .finally(() => setLoading(false));
    }, [headers]);

    useEffect(() => { fetchCodes(); }, [fetchCodes]);

    const handleCreate = async () => {
        if (!form.code.trim()) { toast.error('Code is required'); return; }
        try {
            const body = { code: form.code, discount_percent: parseInt(form.discount_percent), description: form.description };
            if (form.max_uses) body.max_uses = parseInt(form.max_uses);
            if (form.expires_at) body.expires_at = new Date(form.expires_at).toISOString();
            const res = await fetch(`${API}/admin/discount-codes`, {
                method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body)
            });
            if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
            toast.success('Discount code created');
            setShowCreate(false);
            setForm({ code: '', discount_percent: 10, max_uses: '', description: '', expires_at: '' });
            fetchCodes();
        } catch (e) { toast.error(e.message); }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const res = await fetch(`${API}/admin/discount-codes/${deleteTarget.id}`, { 
                method: 'DELETE', 
                headers: { ...headers, 'Content-Type': 'application/json' }
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Delete failed');
            }
            toast.success('Code deleted'); 
            setDeleteTarget(null);
            fetchCodes();
        } catch (e) { 
            console.error('Delete error:', e);
            toast.error(e.message || 'Failed to delete'); 
        } finally {
            setDeleting(false);
        }
    };

    const handleToggle = async (id) => {
        try {
            const res = await fetch(`${API}/admin/discount-codes/${id}/toggle`, { method: 'PUT', headers });
            if (!res.ok) throw new Error();
            const data = await res.json();
            toast.success(data.message); fetchCodes();
        } catch { toast.error('Failed to toggle'); }
    };

    const syncToStripe = async () => {
        try {
            const res = await fetch(`${API}/admin/discount-codes/sync-stripe`, { 
                method: 'POST', 
                headers 
            });
            const data = await res.json();
            if (res.ok) {
                toast.success(data.message);
                if (data.errors?.length) {
                    toast.error(`Some codes failed: ${data.errors.join(', ')}`);
                }
            } else {
                toast.error(data.detail || 'Sync failed');
            }
        } catch { toast.error('Failed to sync'); }
    };

    return (
        <div data-testid="codes-content">
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-muted-foreground">{codes.length} discount code{codes.length !== 1 ? 's' : ''}</p>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={syncToStripe} data-testid="sync-stripe-btn">
                        <RefreshCw className="w-4 h-4 mr-2" />Sync to Stripe
                    </Button>
                    <Dialog open={showCreate} onOpenChange={setShowCreate}>
                        <DialogTrigger asChild>
                            <Button data-testid="create-code-btn"><Plus className="w-4 h-4 mr-2" />New Code</Button>
                        </DialogTrigger>
                    <DialogContent data-testid="create-code-dialog">
                        <DialogHeader><DialogTitle>Create Discount Code</DialogTitle></DialogHeader>
                        <div className="space-y-4 py-2">
                            <div>
                                <Label>Code</Label>
                                <Input placeholder="e.g. RAMADAN25" value={form.code}
                                    onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} className="mt-1" data-testid="code-input" />
                            </div>
                            <div>
                                <Label>Discount %</Label>
                                <Input type="number" min={1} max={100} value={form.discount_percent}
                                    onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))} className="mt-1" data-testid="discount-input" />
                            </div>
                            <div>
                                <Label>Max Uses (optional)</Label>
                                <Input type="number" min={1} placeholder="Unlimited" value={form.max_uses}
                                    onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))} className="mt-1" data-testid="max-uses-input" />
                            </div>
                            <div>
                                <Label>Expiry Date (optional)</Label>
                                <Input type="date" value={form.expires_at}
                                    onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} className="mt-1" data-testid="expiry-input" />
                            </div>
                            <div>
                                <Label>Description</Label>
                                <Input placeholder="Ramadan 2026 special offer" value={form.description}
                                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1" data-testid="description-input" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                            <Button onClick={handleCreate} data-testid="submit-code-btn">Create</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                </div>
            </div>
            <Card className="border-border/50 overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Discount</TableHead>
                            <TableHead className="hidden sm:table-cell">Uses</TableHead>
                            <TableHead className="hidden md:table-cell">Expires</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? Array.from({ length: 3 }).map((_, i) => (
                            <TableRow key={i}>
                                <TableCell><div className="h-4 w-24 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell><div className="h-4 w-12 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell className="hidden sm:table-cell"><div className="h-4 w-12 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell className="hidden md:table-cell"><div className="h-4 w-20 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell><div className="h-5 w-16 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell />
                            </TableRow>
                        )) : codes.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No discount codes yet</TableCell></TableRow>
                        ) : codes.map(c => (
                            <TableRow key={c.id} data-testid={`code-row-${c.id}`}>
                                <TableCell className="font-mono font-medium">{c.code}</TableCell>
                                <TableCell>{c.discount_percent}%</TableCell>
                                <TableCell className="hidden sm:table-cell">{c.times_used}{c.max_uses ? ` / ${c.max_uses}` : ''}</TableCell>
                                <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}
                                </TableCell>
                                <TableCell><Badge variant={c.is_active ? 'default' : 'outline'}>{c.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => handleToggle(c.id)} data-testid={`toggle-code-${c.id}`}><Power className="w-4 h-4" /></Button>
                                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"
                                            onClick={() => setDeleteTarget({ id: c.id, code: c.code })} data-testid={`delete-code-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <DialogContent data-testid="delete-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete Discount Code</DialogTitle>
                    </DialogHeader>
                    <p className="text-muted-foreground py-4">
                        Are you sure you want to delete the code <span className="font-mono font-bold text-foreground">"{deleteTarget?.code}"</span>? This action cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                        <Button variant="destructive" onClick={confirmDelete} disabled={deleting} data-testid="confirm-delete-btn">
                            {deleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function KnowledgeBaseTab({ headers }) {
    const [stats, setStats] = useState(null);
    const [sources, setSources] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [triggeringSource, setTriggeringSource] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsRes, sourcesRes, jobsRes] = await Promise.all([
                fetch(`${API}/admin/knowledge-base/stats`, { headers }),
                fetch(`${API}/admin/knowledge-base/sources`, { headers }),
                fetch(`${API}/admin/knowledge-base/jobs`, { headers })
            ]);
            
            // Helper to safely parse JSON responses
            const safeJson = async (res) => {
                const text = await res.text();
                try {
                    return JSON.parse(text);
                } catch {
                    console.error('Invalid JSON response:', text.substring(0, 100));
                    return null;
                }
            };
            
            if (statsRes.ok) {
                const data = await safeJson(statsRes);
                if (data) setStats(data);
            }
            if (sourcesRes.ok) {
                const data = await safeJson(sourcesRes);
                if (data) setSources(data.sources || []);
            }
            if (jobsRes.ok) {
                const data = await safeJson(jobsRes);
                if (data) setJobs(data.jobs || []);
            }
        } catch (err) {
            toast.error('Failed to load knowledge base data');
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Safe JSON parser helper
    const safeJsonParse = async (res) => {
        try {
            const text = await res.text();
            return JSON.parse(text);
        } catch {
            return null;
        }
    };

    // Auto-refresh jobs every 10 seconds if any are running
    useEffect(() => {
        const hasRunning = jobs.some(j => j.status === 'running' || j.status === 'queued');
        if (!hasRunning) return;
        
        const interval = setInterval(async () => {
            try {
                const [jobsRes, sourcesRes, statsRes] = await Promise.all([
                    fetch(`${API}/admin/knowledge-base/jobs`, { headers }),
                    fetch(`${API}/admin/knowledge-base/sources`, { headers }),
                    fetch(`${API}/admin/knowledge-base/stats`, { headers })
                ]);
                
                if (jobsRes.ok) {
                    const data = await safeJsonParse(jobsRes);
                    if (data) setJobs(data.jobs || []);
                }
                
                // Also refresh sources to update their status
                if (sourcesRes.ok) {
                    const data = await safeJsonParse(sourcesRes);
                    if (data) setSources(data.sources || []);
                }
                
                // Refresh stats to update vector count
                if (statsRes.ok) {
                    const data = await safeJsonParse(statsRes);
                    if (data) setStats(data);
                }
            } catch (err) {
                console.error('Failed to refresh jobs:', err);
            }
        }, 10000);
        
        return () => clearInterval(interval);
    }, [jobs]);

    const triggerScrape = async (sourceId) => {
        setTriggeringSource(sourceId);
        try {
            const res = await fetch(`${API}/admin/knowledge-base/scrape`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ source: sourceId })
            });
            
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Failed to trigger scrape');
            }
            
            const data = await res.json();
            toast.success(data.message);
            
            // Update the source status to show it's running
            setSources(prev => prev.map(s => 
                s.id === sourceId ? { ...s, status: 'running' } : s
            ));
            
            // Refresh data to get the latest job status
            fetchData();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setTriggeringSource(null);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'failed': return <XCircle className="w-4 h-4 text-red-500" />;
            case 'running': return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
            case 'queued': return <Clock className="w-4 h-4 text-yellow-500" />;
            case 'timeout': return <AlertCircle className="w-4 h-4 text-orange-500" />;
            default: return <Clock className="w-4 h-4 text-muted-foreground" />;
        }
    };

    const getStatusBadge = (status) => {
        const variants = {
            completed: 'default',
            failed: 'destructive',
            running: 'secondary',
            queued: 'outline',
            timeout: 'destructive'
        };
        return <Badge variant={variants[status] || 'outline'}>{status}</Badge>;
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Card key={i} className="border-border/50">
                            <CardContent className="pt-5 pb-4">
                                <div className="h-4 w-24 bg-muted rounded mb-3 animate-pulse" />
                                <div className="h-8 w-16 bg-muted rounded animate-pulse" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="knowledge-base-content">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-border/50">
                    <CardContent className="pt-5 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Vectors</span>
                            <Database className="w-4 h-4 text-primary" />
                        </div>
                        <p className="text-2xl font-bold font-heading">
                            {stats?.total_vectors?.toLocaleString() || 0}
                        </p>
                    </CardContent>
                </Card>
                <Card className="border-border/50">
                    <CardContent className="pt-5 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">Completed</span>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                        <p className="text-2xl font-bold font-heading text-green-500">
                            {sources.filter(s => s.status === 'completed').length}/{sources.length}
                        </p>
                    </CardContent>
                </Card>
                <Card className="border-border/50">
                    <CardContent className="pt-5 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">Running Jobs</span>
                            <RefreshCw className="w-4 h-4 text-primary" />
                        </div>
                        <p className="text-2xl font-bold font-heading">
                            {jobs.filter(j => j.status === 'running' || j.status === 'queued').length}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Data Sources */}
            <Card className="border-border/50">
                <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Database className="w-5 h-5" />
                        Data Sources
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {sources.map((source) => {
                            const isRunning = jobs.some(j => j.source === source.id && (j.status === 'running' || j.status === 'queued'));
                            const isBlocked = source.status === 'blocked';
                            const isCompleted = source.status === 'completed';
                            const vectorCount = source.vector_count || 0;
                            
                            return (
                                <div
                                    key={source.id}
                                    className={`p-4 rounded-lg border ${
                                        isBlocked ? 'bg-muted/30 border-border/30' : 
                                        isCompleted ? 'bg-green-500/5 border-green-500/20' : 
                                        'bg-muted/50 border-border/50'
                                    }`}
                                    data-testid={`source-${source.id}`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <h4 className="font-medium">{source.name}</h4>
                                            <p className="text-sm text-muted-foreground mt-1">{source.description}</p>
                                        </div>
                                        {isBlocked ? (
                                            <Badge variant="outline" className="text-orange-500 border-orange-500">Blocked</Badge>
                                        ) : isRunning ? (
                                            <Badge variant="secondary">
                                                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                                Running
                                            </Badge>
                                        ) : isCompleted ? (
                                            <Badge variant="default" className="bg-green-600">
                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                Completed
                                            </Badge>
                                        ) : null}
                                    </div>
                                    
                                    {/* Vector count indicator */}
                                    {vectorCount > 0 && (
                                        <div className="flex items-center gap-2 mb-2 text-xs">
                                            <Database className="w-3 h-3 text-green-500" />
                                            <span className="text-green-600 font-medium">
                                                {vectorCount.toLocaleString()} vectors ingested
                                            </span>
                                        </div>
                                    )}
                                    
                                    <p className="text-xs text-muted-foreground mb-3">{source.estimated_time}</p>
                                    
                                    <Button
                                        size="sm"
                                        variant={isCompleted ? "outline" : "default"}
                                        disabled={isBlocked || isRunning || triggeringSource === source.id}
                                        onClick={() => triggerScrape(source.id)}
                                        data-testid={`trigger-${source.id}`}
                                    >
                                        {triggeringSource === source.id ? (
                                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                        ) : isCompleted ? (
                                            <RefreshCw className="w-4 h-4 mr-2" />
                                        ) : (
                                            <Play className="w-4 h-4 mr-2" />
                                        )}
                                        {isBlocked ? 'Unavailable' : isRunning ? 'In Progress' : isCompleted ? 'Re-sync' : 'Start Scrape'}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Recent Jobs */}
            <Card className="border-border/50">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Recent Jobs
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-jobs">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                </CardHeader>
                <CardContent>
                    {jobs.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No scraping jobs yet. Start one above!</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Source</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="hidden sm:table-cell">Started</TableHead>
                                    <TableHead className="hidden md:table-cell">Completed</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {jobs.slice(0, 10).map((job) => (
                                    <TableRow key={job.id} data-testid={`job-row-${job.id}`}>
                                        <TableCell className="font-medium capitalize">{job.source}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                {getStatusIcon(job.status)}
                                                {getStatusBadge(job.status)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">
                                            {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                                            {job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}


// Color options for ad styling
const AD_COLORS = [
    { name: 'Emerald', bg: 'from-emerald-500/10 to-emerald-600/5', border: 'border-emerald-500/20' },
    { name: 'Amber', bg: 'from-amber-500/10 to-amber-600/5', border: 'border-amber-500/20' },
    { name: 'Blue', bg: 'from-blue-500/10 to-blue-600/5', border: 'border-blue-500/20' },
    { name: 'Purple', bg: 'from-purple-500/10 to-purple-600/5', border: 'border-purple-500/20' },
    { name: 'Rose', bg: 'from-rose-500/10 to-rose-600/5', border: 'border-rose-500/20' },
    { name: 'Teal', bg: 'from-teal-500/10 to-teal-600/5', border: 'border-teal-500/20' },
];

const PLACEMENTS = [
    { id: 'sidebar', name: 'Sidebar', desc: 'Small ad in chat sidebar' },
    { id: 'banner', name: 'Bottom Banner', desc: 'Full-width banner at bottom' },
    { id: 'inline', name: 'Inline', desc: 'Between chat messages' },
];

function AdsManagerTab({ headers }) {
    const [ads, setAds] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingAd, setEditingAd] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        advertiser_name: '',
        advertiser_email: '',
        advertiser_contact: '',
        cta_text: 'Learn More',
        cta_url: '',
        placement: 'sidebar',
        bg_gradient: AD_COLORS[0].bg,
        border_color: AD_COLORS[0].border,
        is_active: true,
        start_date: '',
        end_date: '',
        monthly_rate: '',
        notes: ''
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [adsRes, statsRes] = await Promise.all([
                fetch(`${API}/admin/ads`, { headers }),
                fetch(`${API}/admin/ads/stats/summary`, { headers })
            ]);
            
            if (adsRes.ok) {
                const data = await adsRes.json();
                setAds(data.ads || []);
            }
            if (statsRes.ok) {
                setStats(await statsRes.json());
            }
        } catch (err) {
            toast.error('Failed to load ads');
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            advertiser_name: '',
            advertiser_email: '',
            advertiser_contact: '',
            cta_text: 'Learn More',
            cta_url: '',
            placement: 'sidebar',
            bg_gradient: AD_COLORS[0].bg,
            border_color: AD_COLORS[0].border,
            is_active: true,
            start_date: '',
            end_date: '',
            monthly_rate: '',
            notes: ''
        });
        setEditingAd(null);
        setShowForm(false);
    };

    const handleEdit = (ad) => {
        setFormData({
            title: ad.title || '',
            description: ad.description || '',
            advertiser_name: ad.advertiser_name || '',
            advertiser_email: ad.advertiser_email || '',
            advertiser_contact: ad.advertiser_contact || '',
            cta_text: ad.cta_text || 'Learn More',
            cta_url: ad.cta_url || '',
            placement: ad.placement || 'sidebar',
            bg_gradient: ad.bg_gradient || AD_COLORS[0].bg,
            border_color: ad.border_color || AD_COLORS[0].border,
            is_active: ad.is_active ?? true,
            start_date: ad.start_date || '',
            end_date: ad.end_date || '',
            monthly_rate: ad.monthly_rate || '',
            notes: ad.notes || ''
        });
        setEditingAd(ad);
        setShowForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const payload = {
            ...formData,
            monthly_rate: formData.monthly_rate ? parseFloat(formData.monthly_rate) : null
        };
        
        try {
            const url = editingAd 
                ? `${API}/admin/ads/${editingAd.id}`
                : `${API}/admin/ads`;
            
            const res = await fetch(url, {
                method: editingAd ? 'PUT' : 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (!res.ok) throw new Error('Failed to save ad');
            
            toast.success(editingAd ? 'Ad updated!' : 'Ad created!');
            resetForm();
            fetchData();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleDelete = async (adId) => {
        if (!confirm('Delete this ad?')) return;
        
        try {
            const res = await fetch(`${API}/admin/ads/${adId}`, {
                method: 'DELETE',
                headers
            });
            if (!res.ok) throw new Error('Failed to delete');
            toast.success('Ad deleted');
            fetchData();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleToggle = async (adId) => {
        try {
            const res = await fetch(`${API}/admin/ads/${adId}/toggle`, {
                method: 'POST',
                headers
            });
            if (!res.ok) throw new Error('Failed to toggle');
            fetchData();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const setColorScheme = (color) => {
        setFormData(prev => ({
            ...prev,
            bg_gradient: color.bg,
            border_color: color.border
        }));
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Card key={i} className="border-border/50">
                            <CardContent className="pt-5 pb-4">
                                <div className="h-4 w-24 bg-muted rounded mb-3 animate-pulse" />
                                <div className="h-8 w-16 bg-muted rounded animate-pulse" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="ads-manager-content">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-border/50">
                    <CardContent className="pt-5 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">Total Ads</span>
                            <Megaphone className="w-4 h-4 text-primary" />
                        </div>
                        <p className="text-2xl font-bold font-heading">{stats?.total_ads || 0}</p>
                    </CardContent>
                </Card>
                <Card className="border-border/50">
                    <CardContent className="pt-5 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">Active</span>
                            <Power className="w-4 h-4 text-green-500" />
                        </div>
                        <p className="text-2xl font-bold font-heading">{stats?.active_ads || 0}</p>
                    </CardContent>
                </Card>
                <Card className="border-border/50">
                    <CardContent className="pt-5 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">Impressions</span>
                            <Eye className="w-4 h-4 text-blue-500" />
                        </div>
                        <p className="text-2xl font-bold font-heading">{stats?.total_impressions?.toLocaleString() || 0}</p>
                    </CardContent>
                </Card>
                <Card className="border-border/50">
                    <CardContent className="pt-5 pb-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">Monthly Rev</span>
                            <DollarSign className="w-4 h-4 text-secondary" />
                        </div>
                        <p className="text-2xl font-bold font-heading">${stats?.monthly_revenue?.toLocaleString() || 0}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Create/Edit Form */}
            {showForm && (
                <Card className="border-border/50">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            {editingAd ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                            {editingAd ? 'Edit Ad' : 'Create New Ad'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Ad Title *</Label>
                                    <Input
                                        value={formData.title}
                                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="e.g., Islamic Books Collection"
                                        required
                                        data-testid="ad-title-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Advertiser Name *</Label>
                                    <Input
                                        value={formData.advertiser_name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, advertiser_name: e.target.value }))}
                                        placeholder="e.g., Dar-us-Salam Publishers"
                                        required
                                        data-testid="ad-advertiser-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Description *</Label>
                                <Input
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="e.g., Discover authentic Islamic literature"
                                    required
                                    data-testid="ad-description-input"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label>Advertiser Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.advertiser_email}
                                        onChange={(e) => setFormData(prev => ({ ...prev, advertiser_email: e.target.value }))}
                                        placeholder="contact@advertiser.com"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Advertiser Contact</Label>
                                    <Input
                                        value={formData.advertiser_contact}
                                        onChange={(e) => setFormData(prev => ({ ...prev, advertiser_contact: e.target.value }))}
                                        placeholder="+1 234 567 8900"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Monthly Rate ($)</Label>
                                    <Input
                                        type="number"
                                        value={formData.monthly_rate}
                                        onChange={(e) => setFormData(prev => ({ ...prev, monthly_rate: e.target.value }))}
                                        placeholder="100"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label>CTA Button Text</Label>
                                    <Input
                                        value={formData.cta_text}
                                        onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                                        placeholder="Learn More"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>CTA Link URL</Label>
                                    <Input
                                        value={formData.cta_url}
                                        onChange={(e) => setFormData(prev => ({ ...prev, cta_url: e.target.value }))}
                                        placeholder="https://advertiser.com/offer"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Placement</Label>
                                    <select
                                        value={formData.placement}
                                        onChange={(e) => setFormData(prev => ({ ...prev, placement: e.target.value }))}
                                        className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    >
                                        {PLACEMENTS.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Start Date</Label>
                                    <Input
                                        type="datetime-local"
                                        value={formData.start_date?.slice(0, 16) || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>End Date</Label>
                                    <Input
                                        type="datetime-local"
                                        value={formData.end_date?.slice(0, 16) || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Color Scheme</Label>
                                <div className="flex flex-wrap gap-2">
                                    {AD_COLORS.map((color) => (
                                        <button
                                            key={color.name}
                                            type="button"
                                            onClick={() => setColorScheme(color)}
                                            className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                                                formData.bg_gradient === color.bg 
                                                    ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' 
                                                    : ''
                                            } bg-gradient-to-r ${color.bg} ${color.border}`}
                                        >
                                            {color.name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Internal Notes</Label>
                                <Input
                                    value={formData.notes}
                                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="e.g., Contact renewed until March 2026"
                                />
                            </div>

                            {/* Preview */}
                            <div className="space-y-2">
                                <Label>Preview</Label>
                                <div className={`p-4 rounded-lg bg-gradient-to-r ${formData.bg_gradient} border ${formData.border_color}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground px-1.5 py-0.5 bg-background/50 rounded">Ad</span>
                                        <h4 className="font-medium text-sm">{formData.title || 'Ad Title'}</h4>
                                    </div>
                                    <p className="text-xs text-muted-foreground">{formData.description || 'Ad description'}</p>
                                </div>
                            </div>

                            <div className="flex gap-2 pt-2">
                                <Button type="submit" data-testid="ad-submit-btn">
                                    {editingAd ? 'Update Ad' : 'Create Ad'}
                                </Button>
                                <Button type="button" variant="outline" onClick={resetForm}>
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Ads List */}
            <Card className="border-border/50">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Megaphone className="w-5 h-5" />
                        All Ads ({ads.length})
                    </CardTitle>
                    <Button onClick={() => { resetForm(); setShowForm(true); }} data-testid="create-ad-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        New Ad
                    </Button>
                </CardHeader>
                <CardContent>
                    {ads.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No ads yet. Create your first ad to start monetizing!</p>
                    ) : (
                        <div className="space-y-4">
                            {ads.map((ad) => (
                                <div 
                                    key={ad.id}
                                    className={`p-4 rounded-lg border ${ad.is_active ? 'border-border/50' : 'border-border/30 opacity-60'}`}
                                    data-testid={`ad-row-${ad.id}`}
                                >
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h4 className="font-medium">{ad.title}</h4>
                                                <Badge variant={ad.is_active ? 'default' : 'outline'}>
                                                    {ad.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                                <Badge variant="secondary" className="capitalize">{ad.placement}</Badge>
                                            </div>
                                            <p className="text-sm text-muted-foreground mb-2">{ad.description}</p>
                                            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                    <Users className="w-3 h-3" />
                                                    {ad.advertiser_name}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Eye className="w-3 h-3" />
                                                    {ad.impressions?.toLocaleString() || 0} impressions
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <MousePointer className="w-3 h-3" />
                                                    {ad.clicks?.toLocaleString() || 0} clicks
                                                </span>
                                                {ad.monthly_rate && (
                                                    <span className="flex items-center gap-1">
                                                        <DollarSign className="w-3 h-3" />
                                                        ${ad.monthly_rate}/mo
                                                    </span>
                                                )}
                                                {ad.end_date && (
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        Ends: {new Date(ad.end_date).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {ad.cta_url && (
                                                <Button variant="ghost" size="icon" asChild>
                                                    <a href={ad.cta_url} target="_blank" rel="noopener noreferrer">
                                                        <ExternalLink className="w-4 h-4" />
                                                    </a>
                                                </Button>
                                            )}
                                            <Button 
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => handleToggle(ad.id)}
                                                data-testid={`toggle-ad-${ad.id}`}
                                            >
                                                <Power className={`w-4 h-4 ${ad.is_active ? 'text-green-500' : 'text-muted-foreground'}`} />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="icon"
                                                onClick={() => handleEdit(ad)}
                                                data-testid={`edit-ad-${ad.id}`}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="text-destructive hover:text-destructive"
                                                onClick={() => handleDelete(ad.id)}
                                                data-testid={`delete-ad-${ad.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}


const BLOG_CATEGORIES = [
    { value: "general", label: "General" },
    { value: "fiqh", label: "Fiqh" },
    { value: "quran", label: "Quran" },
    { value: "hadith", label: "Hadith" },
    { value: "aqeedah", label: "Aqeedah" },
    { value: "lifestyle", label: "Lifestyle" },
];

function BlogTab({ headers }) {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEditor, setShowEditor] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [search, setSearch] = useState('');
    const [previewMode, setPreviewMode] = useState(false);
    const [contentType, setContentType] = useState('html');

    const emptyForm = {
        title: '', slug: '', content: '', excerpt: '',
        category: 'general', tags: '', meta_title: '',
        meta_description: '', featured_image: '', published: false,
    };
    const [form, setForm] = useState(emptyForm);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/blog/posts?published_only=false`, { headers });
            if (res.ok) {
                const data = await res.json();
                setPosts(data.posts || []);
            }
        } catch { toast.error('Failed to load posts'); }
        finally { setLoading(false); }
    }, [headers]);

    useEffect(() => { fetchPosts(); }, [fetchPosts]);

    const generateSlug = (title) =>
        title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    const handleTitleChange = (val) => {
        setForm(f => ({
            ...f,
            title: val,
            slug: editingPost ? f.slug : generateSlug(val),
        }));
    };

    const openCreate = () => {
        setEditingPost(null);
        setForm(emptyForm);
        setContentType('html');
        setPreviewMode(false);
        setShowEditor(true);
    };

    const openEdit = (post) => {
        setEditingPost(post);
        setForm({
            title: post.title || '',
            slug: post.slug || '',
            content: post.content || '',
            excerpt: post.excerpt || '',
            category: post.category || 'general',
            tags: (post.tags || []).join(', '),
            meta_title: post.meta_title || '',
            meta_description: post.meta_description || '',
            featured_image: post.featured_image || '',
            published: post.published ?? false,
        });
        setContentType(post.content_type || (post.content?.trim().startsWith('<') ? 'html' : 'markdown'));
        setPreviewMode(false);
        setShowEditor(true);
    };

    const handleSave = async () => {
        if (!form.title.trim() || !form.content.trim()) {
            toast.error('Title and content are required');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                title: form.title,
                slug: form.slug || generateSlug(form.title),
                content: form.content,
                content_type: contentType,
                excerpt: form.excerpt || (contentType === 'html' ? form.content.replace(/<[^>]*>/g, '').slice(0, 200) : form.content.slice(0, 200)),
                category: form.category,
                tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
                meta_title: form.meta_title || form.title,
                meta_description: form.meta_description || form.excerpt || (contentType === 'html' ? form.content.replace(/<[^>]*>/g, '').slice(0, 160) : form.content.slice(0, 160)),
                featured_image: form.featured_image,
                published: form.published,
            };

            const url = editingPost
                ? `${API}/admin/blog/posts/${editingPost.id}`
                : `${API}/admin/blog/posts`;
            const res = await fetch(url, {
                method: editingPost ? 'PUT' : 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to save');
            }
            toast.success(editingPost ? 'Post updated' : 'Post created');
            setShowEditor(false);
            fetchPosts();
        } catch (e) { toast.error(e.message); }
        finally { setSaving(false); }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const res = await fetch(`${API}/admin/blog/posts/${deleteTarget.id}`, {
                method: 'DELETE', headers,
            });
            if (!res.ok) throw new Error('Failed to delete');
            toast.success('Post deleted');
            setDeleteTarget(null);
            fetchPosts();
        } catch (e) { toast.error(e.message); }
        finally { setDeleting(false); }
    };

    const togglePublish = async (post) => {
        try {
            const res = await fetch(`${API}/admin/blog/posts/${post.id}`, {
                method: 'PUT',
                headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ published: !post.published }),
            });
            if (!res.ok) throw new Error('Failed to toggle');
            toast.success(post.published ? 'Post unpublished' : 'Post published');
            fetchPosts();
        } catch (e) { toast.error(e.message); }
    };

    const filtered = posts.filter(p =>
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.category?.toLowerCase().includes(search.toLowerCase())
    );

    // Simple markdown preview renderer
    const renderPreview = (text) => {
        if (!text) return <p className="text-muted-foreground italic">Nothing to preview yet...</p>;
        return text.split(/\n\n+/).map((block, i) => {
            const t = block.trim();
            if (!t) return null;
            if (t.startsWith('### ')) return <h3 key={i} className="text-lg font-heading font-semibold text-foreground mt-4 mb-2">{t.slice(4)}</h3>;
            if (t.startsWith('## ')) return <h2 key={i} className="text-xl font-heading font-semibold text-foreground mt-6 mb-3">{t.slice(3)}</h2>;
            if (t.startsWith('# ')) return <h1 key={i} className="text-2xl font-heading font-bold text-foreground mt-6 mb-3">{t.slice(2)}</h1>;
            if (t.startsWith('> ')) return <blockquote key={i} className="border-l-4 border-primary/40 pl-4 py-2 my-3 bg-primary/5 rounded-r-lg italic">{t.slice(2)}</blockquote>;
            return <p key={i} className="text-muted-foreground leading-relaxed mb-3">{
                t.split(/(\*\*[^*]+\*\*)/).map((part, j) =>
                    part.startsWith('**') && part.endsWith('**')
                        ? <strong key={j} className="text-foreground">{part.slice(2, -2)}</strong>
                        : part
                )
            }</p>;
        });
    };

    // Editor view
    if (showEditor) {
        return (
            <div className="space-y-6" data-testid="blog-editor">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => setShowEditor(false)} data-testid="blog-editor-back">
                            <ChevronLeft className="w-4 h-4 mr-1" /> Back
                        </Button>
                        <h2 className="text-lg font-heading font-semibold">
                            {editingPost ? 'Edit Post' : 'New Blog Post'}
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPreviewMode(!previewMode)} data-testid="blog-toggle-preview">
                            {previewMode ? <Edit className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                            {previewMode ? 'Edit' : 'Preview'}
                        </Button>
                        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="blog-save-btn">
                            <Save className="w-4 h-4 mr-1" />
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main content area */}
                    <div className="lg:col-span-2 space-y-4">
                        <div>
                            <Label>Title</Label>
                            <Input
                                value={form.title}
                                onChange={e => handleTitleChange(e.target.value)}
                                placeholder="Your blog post title"
                                className="mt-1 text-lg font-medium"
                                data-testid="blog-title-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Slug</Label>
                            <Input
                                value={form.slug}
                                onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                                placeholder="url-friendly-slug"
                                className="mt-1 font-mono text-sm"
                                data-testid="blog-slug-input"
                            />
                        </div>

                        {previewMode ? (
                            <Card className="border-border/50">
                                <CardContent className="p-6">
                                    <h1 className="text-2xl font-heading font-bold text-foreground mb-4">{form.title || 'Untitled'}</h1>
                                    {contentType === 'html' ? (
                                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.content) }} className="prose prose-sm dark:prose-invert max-w-none" />
                                    ) : renderPreview(form.content)}
                                </CardContent>
                            </Card>
                        ) : (
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <Label>Content</Label>
                                    <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
                                        <button type="button"
                                            onClick={() => setContentType('html')}
                                            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${contentType === 'html' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                            data-testid="blog-mode-html">
                                            HTML
                                        </button>
                                        <button type="button"
                                            onClick={() => setContentType('markdown')}
                                            className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${contentType === 'markdown' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                            data-testid="blog-mode-markdown">
                                            Markdown
                                        </button>
                                    </div>
                                </div>
                                <textarea
                                    value={form.content}
                                    onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                                    placeholder={contentType === 'html'
                                        ? 'Paste your HTML blog content here...\n\n<h2>Your Heading</h2>\n<p>Your paragraph text...</p>'
                                        : 'Write your blog post content here...\n\n# Heading 1\n## Heading 2\n**bold text**\n> blockquote'}
                                    className="mt-1 w-full min-h-[400px] p-4 rounded-md border border-input bg-background text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                                    data-testid="blog-content-input"
                                />
                                {contentType === 'html' && (
                                    <p className="text-xs text-muted-foreground mt-1">Paste full HTML from Claude or any editor. It will be rendered as-is on the blog page.</p>
                                )}
                            </div>
                        )}

                        <div>
                            <Label>Excerpt</Label>
                            <textarea
                                value={form.excerpt}
                                onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                                placeholder="Brief summary for blog listing cards (auto-generated from content if empty)"
                                className="mt-1 w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                                data-testid="blog-excerpt-input"
                            />
                        </div>
                    </div>

                    {/* Sidebar settings */}
                    <div className="space-y-4">
                        <Card className="border-border/50">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm">Publish Settings</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm">Status</Label>
                                    <button
                                        type="button"
                                        onClick={() => setForm(f => ({ ...f, published: !f.published }))}
                                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                            form.published
                                                ? 'bg-green-500/10 text-green-500 border border-green-500/30'
                                                : 'bg-muted text-muted-foreground border border-border'
                                        }`}
                                        data-testid="blog-publish-toggle"
                                    >
                                        {form.published ? 'Published' : 'Draft'}
                                    </button>
                                </div>

                                <div>
                                    <Label className="text-sm">Category</Label>
                                    <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                                        <SelectTrigger className="mt-1" data-testid="blog-category-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {BLOG_CATEGORIES.map(c => (
                                                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div>
                                    <Label className="text-sm">Tags (comma separated)</Label>
                                    <Input
                                        value={form.tags}
                                        onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                                        placeholder="e.g. ramadan, fasting, fiqh"
                                        className="mt-1"
                                        data-testid="blog-tags-input"
                                    />
                                </div>

                                <div>
                                    <Label className="text-sm">Featured Image URL</Label>
                                    <Input
                                        value={form.featured_image}
                                        onChange={e => setForm(f => ({ ...f, featured_image: e.target.value }))}
                                        placeholder="https://..."
                                        className="mt-1"
                                        data-testid="blog-image-input"
                                    />
                                    {form.featured_image && (
                                        <img src={form.featured_image} alt="Preview" className="mt-2 rounded-md max-h-32 object-cover w-full" />
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-border/50">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm">SEO</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div>
                                    <Label className="text-xs text-muted-foreground">Meta Title</Label>
                                    <Input
                                        value={form.meta_title}
                                        onChange={e => setForm(f => ({ ...f, meta_title: e.target.value }))}
                                        placeholder="SEO title (uses post title if empty)"
                                        className="mt-1"
                                        data-testid="blog-meta-title-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs text-muted-foreground">Meta Description</Label>
                                    <textarea
                                        value={form.meta_description}
                                        onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))}
                                        placeholder="SEO description (uses excerpt if empty)"
                                        className="mt-1 w-full min-h-[60px] p-2 rounded-md border border-input bg-background text-sm resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                                        data-testid="blog-meta-desc-input"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">{(form.meta_description || '').length}/160</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    // List view
    return (
        <div data-testid="blog-content">
            <div className="flex flex-col sm:flex-row gap-3 mb-4 items-start sm:items-center justify-between">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search posts..." className="pl-9" value={search}
                        onChange={e => setSearch(e.target.value)} data-testid="blog-search-input" />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{posts.length} post{posts.length !== 1 ? 's' : ''}</span>
                    <Button onClick={openCreate} data-testid="blog-create-btn">
                        <Plus className="w-4 h-4 mr-2" /> New Post
                    </Button>
                </div>
            </div>

            <Card className="border-border/50 overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead className="hidden sm:table-cell">Category</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="hidden md:table-cell">Views</TableHead>
                            <TableHead className="hidden lg:table-cell">Created</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? Array.from({ length: 3 }).map((_, i) => (
                            <TableRow key={i}>
                                <TableCell><div className="h-4 w-40 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell className="hidden sm:table-cell"><div className="h-4 w-16 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell><div className="h-5 w-16 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell className="hidden md:table-cell"><div className="h-4 w-10 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell className="hidden lg:table-cell"><div className="h-4 w-20 bg-muted rounded animate-pulse" /></TableCell>
                                <TableCell />
                            </TableRow>
                        )) : filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                                    {posts.length === 0 ? (
                                        <div>
                                            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                                            <p>No blog posts yet</p>
                                            <p className="text-xs mt-1">Click "New Post" to create your first SEO blog article.</p>
                                        </div>
                                    ) : 'No posts match your search.'}
                                </TableCell>
                            </TableRow>
                        ) : filtered.map(post => (
                            <TableRow key={post.id} data-testid={`blog-row-${post.id}`}>
                                <TableCell>
                                    <div>
                                        <p className="font-medium text-sm line-clamp-1">{post.title}</p>
                                        <p className="text-xs text-muted-foreground font-mono">/{post.slug}</p>
                                    </div>
                                </TableCell>
                                <TableCell className="hidden sm:table-cell">
                                    <Badge variant="outline" className="capitalize text-xs">{post.category}</Badge>
                                </TableCell>
                                <TableCell>
                                    {post.published ? (
                                        <Badge variant="default" className="bg-green-600 text-xs">
                                            <Globe className="w-3 h-3 mr-1" /> Live
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="text-xs">
                                            <EyeOff className="w-3 h-3 mr-1" /> Draft
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                                    {post.views || 0}
                                </TableCell>
                                <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">
                                    {new Date(post.created_at).toLocaleDateString()}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex justify-end gap-1">
                                        <Button variant="ghost" size="icon" onClick={() => togglePublish(post)}
                                            title={post.published ? 'Unpublish' : 'Publish'}
                                            data-testid={`blog-toggle-${post.id}`}>
                                            {post.published ? <EyeOff className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(post)}
                                            data-testid={`blog-edit-${post.id}`}>
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"
                                            onClick={() => setDeleteTarget(post)}
                                            data-testid={`blog-delete-${post.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
                <DialogContent data-testid="blog-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete Blog Post</DialogTitle>
                    </DialogHeader>
                    <p className="text-muted-foreground py-4">
                        Are you sure you want to delete <strong className="text-foreground">"{deleteTarget?.title}"</strong>? This cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                        <Button variant="destructive" onClick={confirmDelete} disabled={deleting} data-testid="blog-confirm-delete-btn">
                            {deleting ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
